import differenceInCalendarMonths from 'date-fns/differenceInCalendarMonths';
import { inject } from 'inversify';
import find from 'lodash/find';
import head from 'lodash/head';
import last from 'lodash/last';
import sortBy from 'lodash/sortBy';
import EventEmitter from 'wolfy87-eventemitter';
import { META_EVENTS } from '../../../constants/CONTROLLERS_EVENT_TYPES';
import MESSAGE_TYPES from '../../../constants/MESSAGE_TYPES';
import { getNextMonth, getTimesInDays, parseDate } from '../../../helpers/dates';
import { IMeta } from '../../../types/widget';
import Messager from '../../messager';
import type { IStateController } from '../state/state.interface';
import { STATE_MODULE_IDS } from '../state/state.types';

export class MetaController extends EventEmitter {
  meta: IMeta = {};

  selectedEvent?: number = null;

  selectedDate?: string = null;

  leftoverThreshold?: number = null;

  @inject(STATE_MODULE_IDS.STATE_CONTROLLER) public stateController: IStateController;

  get selectedDateIndex() {
    return this.meta.dates.findIndex((date) => date.date === this.selectedDate);
  }

  get siblingsDates() {
    const currentIndex = this.selectedDateIndex;
    const leftDate = this.meta.dates[currentIndex - 1];
    const rightDate = this.meta.dates[currentIndex + 1];

    return {
      leftDate,
      rightDate,
    };
  }

  addMeta(schemeData) {
    const { settings } = window.Widget;
    let { meta } = schemeData;
    let { dates } = schemeData;

    if (!dates && window.Widget.settings.dates) {
      dates = window.Widget.settings.dates;
    }

    if (!dates && window.Widget.settings.eventData.dates) {
      dates = window.Widget.settings.eventData.dates.map((date) => {
        return { id: date.id, date: `${date.date_start} ${date.time_start}` };
      });
    }

    if (!meta && settings.eventData) {
      meta = {
        hall_layout: {
          meta: {
            bg: settings.bg,
            width: settings.width,
            height: settings.height,
          },
        },
        title: settings.eventData.title,
        bg: settings.bg,
        width: settings.width,
        height: settings.height,
        place: settings.eventData.place,
      };
    }

    if (settings.hintText) {
      meta.hintText = settings.hintText;
    }

    if (settings.is_pushkin) {
      meta.is_pushkin = settings.is_pushkin;
    }

    if (schemeData.meta?.hint_text && schemeData.meta?.is_show_hint) {
      meta.hintText = schemeData.meta.hint_text;
    }

    this.meta = meta;
    let defaultDate = window.Widget.settings.selectedDate;

    if (dates && dates.length) {
      const isExist = dates.find((date) => date.date === defaultDate);

      this.meta.dates = sortBy(
        dates.map((data) => {
          const datePrice = find(schemeData.prices || [], (o) => o.id === data.id);

          return {
            ...data,
            ...(datePrice && {
              ...datePrice,
              has_tickets: true,
            }),
            name: data.date,
          };
        }),
        'date',
      );

      if (!isExist) {
        defaultDate = head(this.meta.dates).date;
      }

      const selectedDate = defaultDate || head(this.meta.dates).date;
      const selectedEvent = dates.find((data) => data.date === selectedDate);
      this.selectedEvent = selectedEvent && selectedEvent.id;
      this.selectedDate = selectedDate;
      this.emit(MESSAGE_TYPES.GET_SELECTED_DATE, this.selectedDate);

      const minDate = new Date(parseDate(head(this.meta.dates).date));
      const maxDate = new Date(parseDate(last(this.meta.dates).date));
      const monthsCount = differenceInCalendarMonths(maxDate, minDate);
      const nextMonths = [...Array(monthsCount + 1)].map((_, i) => {
        const year = minDate.getFullYear();
        const month = minDate.getMonth() + i;
        const { nextMonth, nextYear } = getNextMonth({ year, month, delta: 1 });

        return {
          month: nextMonth,
          year: nextYear,
        };
      });

      const { leftDate, rightDate } = this.siblingsDates;

      this.meta = {
        ...this.meta,
        minDate,
        maxDate,
        nextMonths,
        leftDate,
        rightDate,
        daysTimes: getTimesInDays({
          dates: this.meta.dates,
          convertToAmPmTime: window.Widget.settings.convertToAmPmTime,
          lang: window.Widget.settings.lang,
        }),
        isPeriodical: dates?.length > 1,
      };
    }

    this.emit(META_EVENTS.INIT);
  }

  get canShowMobilePopup() {
    const { isDragging } = this.stateController;
    const { currentZoom } = window.Widget.zoomController;
    const middleZoom = window.Widget.zoomController.getMiddleZoom();

    return !(currentZoom < middleZoom || isDragging);
  }

  selectEvent(id) {
    this.selectedEvent = id;
    this.selectedDate = this.meta.dates.find((data) => data.id === id).date;

    const { leftDate, rightDate } = this.siblingsDates;

    this.meta.leftDate = leftDate;
    this.meta.rightDate = rightDate;

    Messager.send({
      type: MESSAGE_TYPES.ON_CHANGE_DATE,
      date: this.selectedDate,
    });

    this.emit(META_EVENTS.SELECT_DATE);
  }

  setSelectDate(date) {
    this.selectedEvent = this.meta.dates.find((data) => data.date === date).id;
    this.selectedDate = date;

    const { leftDate, rightDate } = this.siblingsDates;

    this.meta.leftDate = leftDate;
    this.meta.rightDate = rightDate;

    this.emit(META_EVENTS.SELECT_DATE);
    this.emit(META_EVENTS.SET_SELECTED_DATE, date);
  }

  updateLeftoverThreshold(leftoverThreshold: number = 0) {
    const { leftoverThreshold: settingsLeftoverThreshold } = window.Widget.settings;

    this.leftoverThreshold = settingsLeftoverThreshold
      ? settingsLeftoverThreshold * 100
      : leftoverThreshold;
  }
}
